import { IntakeDatesResponse, IntakeLocationsResponse } from "toptaal-shared"
import apiClient from "../client"
import type {
  IntakeFiltersApiRequest,
  IntakeFiltersApiResponse,
  CreateIntakeApiRequest,
  CreateIntakeApiResponse,
  Intake,
  FetchIntakeByIdApiResponse,
  IntakersResponse,
  AvailableIntakeStartTimesApiResponse,
  AvailableIntakeEndTimesApiResponse,
} from "../types/intake"
import { IntakersRequestParams } from "../types/teachers"

export async function fetchIntakeFilters({
  filters,
  page = 1,
  pageSize = 10,
  search = "",
  sortDirection = "asc",
  sortField = "id",
}: IntakeFiltersApiRequest) {
  return await apiClient
    .post<IntakeFiltersApiResponse>("intakes/filters", {
      json: {
        filters,
        page,
        pageSize,
        search,
        sortDirection,
        sortField,
      },
    })
    .json()
}

export async function createIntake(data: CreateIntakeApiRequest) {
  return await apiClient
    .post<CreateIntakeApiResponse>("intakes", {
      json: data,
    })
    .json()
}

export async function deleteIntake(intakeId: Intake["id"]) {
  return await apiClient.delete(`intakes/${intakeId}`).json()
}

export async function editIntake({
  intakeId,
  intake,
}: {
  intakeId: Intake["id"]
  intake: CreateIntakeApiRequest["intake"]
}) {
  return await apiClient
    .put<CreateIntakeApiResponse>(`intakes/${intakeId}`, {
      json: { intake },
    })
    .json()
}

export async function fetchIntakeById({
  intakeId,
}: {
  intakeId: Intake["id"]
}) {
  return await apiClient
    .get<FetchIntakeByIdApiResponse>(`intakes/${intakeId}`)
    .json()
}

export async function getFilteredIntakers({
  locationId,
  projectTypes,
  startTime,
  endTime,
  date,
  intakeId,
}: IntakersRequestParams) {
  return await apiClient
    .post<IntakersResponse>("intakes/new/available_intakers", {
      json: { locationId, projectTypes, startTime, endTime, date, intakeId },
    })
    .json()
}

export async function approveIntakePresence({
  intakeId,
}: {
  intakeId: Intake["id"]
}) {
  return await apiClient.post(`intakes/${intakeId}/approve_presence`).json()
}

export async function getAvailableIntakeStartTimes({
  classroomIds,
  date,
  intakeId,
}: {
  classroomIds: number[]
  date?: string
  intakeId?: Intake["id"]
}) {
  return await apiClient
    .post<AvailableIntakeStartTimesApiResponse>(`intakes/new/available_times`, {
      json: {
        classroomIds,
        date,
        intakeId,
      },
    })
    .json()
}

export async function getAvailableIntakeEndTimes({
  classroomIds,
  date,
  startTime,
  intakeId,
}: {
  classroomIds: number[]
  date?: string
  startTime?: Intake["startTime"]
  intakeId?: Intake["id"]
}) {
  return await apiClient
    .post<AvailableIntakeEndTimesApiResponse>(
      `intakes/new/available_end_times`,
      {
        json: {
          classroomIds,
          date,
          startTime,
          intakeId,
        },
      },
    )
    .json()
}

export const getClosestIntakeLocations = async ({
  studentId,
  page,
}: {
  studentId: number
  page?: number
}) => {
  return await apiClient
    .get<IntakeLocationsResponse>("intakes/locations/closest", {
      searchParams: {
        student_id: studentId,
        page,
      },
    })
    .json()
}

export const getLocationIntakeDates = async ({
  locationId,
  studentId,
}: {
  locationId: number
  studentId?: number
}) => {
  return await apiClient
    .get<IntakeDatesResponse>(`intakes/locations/${locationId}`, {
      searchParams: {
        id: locationId,
        student_id: studentId,
      },
    })
    .json()
}

export const scheduleStudentIntake = async ({
  intakeId,
  studentSpoId,
}: {
  intakeId: number
  studentSpoId?: number
}) => {
  return await apiClient
    .post(`intakes/${intakeId}/students/${studentSpoId}`)
    .json()
}
